import {
  cancelMembership,
  clearGetAllMembers,
  deleteMember,
  getAllMembers,
} from "../../../Redux/Members/MembersActions";
import {
  CellClickedEvent,
  ColDef,
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
} from "ag-grid-community";
import ConfirmationModal from "../../../Components/Common/ConfirmationModal/ConfirmationModal";
import { getAllBuildings } from "../../../Redux/Buildings/BuildingActions";
import Loading from "../../../Components/Common/Loading/Loading";
import { Menu, MenuButton, MenuItem } from "@szhsin/react-menu";
import { AppDispatch, RootState } from "../../../Redux/store";
import exportToExcel from "../../../Util/exportToExcel";
import { useDispatch, useSelector } from "react-redux";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router";
import { HiDotsVertical } from "react-icons/hi";
import { IoCloseSharp } from "react-icons/io5";
import { AgGridReact } from "ag-grid-react";
import styles from "./Members.module.scss";
import moment from "moment";

interface MembersType {
  buildingId: number;
  buildingName: string;
  email: string;
  firstName: string;
  idNumber: string;
  isActive: boolean;
  lastName: string;
  lastPaymentDate: string;
  leaseUnit: string;
  memberId: number;
  memberSince: string;
  packageName: string | null;
  phonenumber: string | null;
  lastLoginTime: string;
  status: string;
  subscriptionStatus: string;
}

const defaultColDef: ColDef = {
  resizable: true,
  sortable: true,
  filter: false,
  wrapText: true,
  autoHeight: true,
  unSortIcon: true,
};

function Members() {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [buildingFilter, setBuildingFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [
    cancelMembershipConfirmationModalOpen,
    setCancelMembershipConfirmationModalOpen,
  ] = useState(false);
  const [cancelMembershipId, setCancelMembershipId] = useState<number>(-1);

  const [
    deactivateMembershipConfirmationModalOpen,
    setDeactivateMembershipConfirmationModalOpen,
  ] = useState(false);
  const [deactivateMembershipId, setDeactivateMembershipId] =
    useState<number>(-1);
  const {
    members,
    membersLoading,
    cancelMembershipLoading,
    deleteMemberLoading,
  } = useSelector((state: RootState) => state.members);

  const { buildings } = useSelector((state: RootState) => state.buildings);

  const [rowData, setRowData] = useState<MembersType[]>([]);
  const [gridApi, setGridApi] = useState<any>(null);
  const [showMembersOnly, setShowMembersOnly] = useState<boolean>(false);

  const gridOptions: GridOptions = {
    onCellClicked: (event: CellClickedEvent<MembersType>) => {
      if (event?.column?.getColId() !== "actions") {
        navigate(`/admin/members/detail/${event?.data?.memberId}`);
      }
    },
  };

  const applyFilters = (rowData: MembersType[]) => {
    let array = [...rowData];

    if (search?.length > 3) {
      array = array.filter(
        (member: MembersType) =>
          member?.firstName?.toLowerCase()?.includes(search?.toLowerCase()) ||
          member?.lastName?.toLowerCase()?.includes(search?.toLowerCase()) ||
          member?.email?.toLowerCase()?.includes(search?.toLowerCase()) ||
          member?.idNumber?.toLowerCase()?.includes(search?.toLowerCase()) ||
          member?.buildingName
            ?.toLowerCase()
            ?.includes(search?.toLowerCase()) ||
          member?.status?.toLowerCase()?.includes(search?.toLowerCase()) ||
          member?.packageName?.toLowerCase()?.includes(search?.toLowerCase()),
      );
    }

    if (buildingFilter) {
      array = array.filter(
        (member: MembersType) => member?.buildingName === buildingFilter,
      );
    }

    if (statusFilter) {
      array = array.filter(
        (member: MembersType) =>
          member?.status?.toLowerCase() === statusFilter?.toLowerCase(),
      );
    }

    if (showMembersOnly) {
      array = array.filter(
        (member: MembersType) => member?.memberSince !== null,
      );
    }

    return array;
  };

  const filteredRowData = applyFilters(rowData);

  const columnDefs: ColDef<MembersType>[] = [
    {
      headerName: "Name",
      flex: 1,
      minWidth: 150,
      valueGetter: (params) =>
        (params?.data?.firstName || "") + " " + (params?.data?.lastName || ""),
    },
    {
      headerName: "Email",
      field: "email",
      flex: 1,
      minWidth: 150,
    },
    {
      headerName: "Building",
      field: "buildingName",
      flex: 1,
      minWidth: 150,
    },
    {
      headerName: "Last Login",
      field: "lastLoginTime",
      flex: 1,
      minWidth: 150,
      valueGetter: (params) => {
        return params?.data?.lastLoginTime
          ? moment(params?.data?.lastLoginTime).format("MM/DD/YYYY HH:mm:ss")
          : "N/A";
      },
    },
    {
      headerName: "Package",
      field: "packageName",
      flex: 1,
      minWidth: 150,
    },

    {
      headerName: "Subscription Status",
      field: "subscriptionStatus",
      flex: 1,
      minWidth: 150,
    },

    {
      headerName: "Status",
      field: "status",
      flex: 1,
      minWidth: 100,
      cellClass: (params) => {
        return params?.data?.status === "Active"
          ? "CompletedText"
          : params?.data?.status === "InActive"
            ? "pendingText"
            : "text-black-500";
      },
    },
    {
      headerName: "Member Since",
      valueGetter: (params) =>
        params?.data?.memberSince
          ? moment(params?.data?.memberSince).format("MM/DD/YYYY")
          : "",
      flex: 1,
      minWidth: 150,
    },
    {
      headerName: "Last Payment",
      valueGetter: (params) =>
        params?.data?.lastPaymentDate
          ? moment(params?.data?.lastPaymentDate).format("MM/DD/YYYY")
          : "",
      flex: 1,
      minWidth: 150,
    },
    {
      width:20,
      colId: "actions",
      sortable: false,
      suppressAutoSize: true,
      suppressMovable: true,
      suppressNavigable: true,
      editable: false,
      cellStyle: {
        pointerEvents: "all",
        userSelect: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
      },
      cellRenderer: (params: ICellRendererParams) => {
        return (
          <div className={styles.iconContainer}>
            <Menu
              direction="left"
              menuButton={
                <MenuButton>
                  <HiDotsVertical className={styles.icon} />
                </MenuButton>
              }
              transition
              portal
            >
              <MenuItem
                onClick={(e) => {
                  navigate(`/admin/members/edit/${params?.data?.memberId}`);
                }}
              >
                Edit
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setDeactivateMembershipConfirmationModalOpen(true);
                  setDeactivateMembershipId(params?.data?.memberId);
                }}
              >
                Deactivate
              </MenuItem>

              <MenuItem
                onClick={() => {
                  setCancelMembershipConfirmationModalOpen(true);
                  setCancelMembershipId(params?.data?.memberId);
                }}
              >
                Cancel Membership
              </MenuItem>
            </Menu>
          </div>
        );
      },
    },
  ];

  const memberDeactivateHandler = (id: string) => {
    if (!deleteMemberLoading && id) {
      const successHandler = () => {
        setDeactivateMembershipConfirmationModalOpen(false);
        setDeactivateMembershipId(-1);
      };
      dispatch(deleteMember({ memberId: Number(id), successHandler }));
    }
  };

  const cancelMembershipHandler = (id: string) => {
    if (!cancelMembershipLoading && id) {
      const successHandler = () => {
        setCancelMembershipConfirmationModalOpen(false);
        setCancelMembershipId(-1);
      };
      dispatch(
        cancelMembership({
          memberId: Number(id),
          successHandler,
        }),
      );
    }
  };

  const onGridReady = (params: GridReadyEvent) => {
    setGridApi(params?.api);
    params?.api?.sizeColumnsToFit();
  };

  useEffect(() => {
    if (!gridApi) return;

    const handleResize = () => {
      gridApi?.sizeColumnsToFit();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [gridApi]);

  useEffect(() => {
    dispatch(getAllMembers());
    dispatch(getAllBuildings());

    return () => {
      dispatch(clearGetAllMembers());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (members) {
      setRowData(members);
    }
  }, [members]);

  if (membersLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingIcon}>
          <Loading />
        </div>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className={styles.mainContainer}>
        <div className={styles.topContainer}>
          <div className={styles.headingContainer}>
            <h1 className={styles.heading}>Manage Members</h1>
          </div>
          <div className={styles.buttonContainer}>
            {filteredRowData?.length > 0 && (
              <button
                className={styles.button}
                onClick={() =>
                  exportToExcel({
                    data: filteredRowData || [],
                    fileName: "Members",
                  })
                }
              >
                Export
              </button>
            )}
            <Link to="/admin/members/add" className={styles.buttonContainer}>
              <button className={styles.button}>Add Member</button>
            </Link>
          </div>
        </div>
        <div className={styles.bodyContainer}>
          <div className={styles.doubleInputContainer}>
            <div className={styles.searchContainer}>
              <div className={styles.searchContainerInner}>
                <input
                  type="text"
                  className={styles.input}
                  placeholder="Search"
                  value={search}
                  onChange={(e) => setSearch(e?.target?.value || "")}
                />
                <div
                  className={styles.iconContainer}
                  onClick={() => setSearch("")}
                >
                  <IoCloseSharp className={styles.icon} />
                </div>
              </div>
            </div>
            <div className={styles.filtersContainer}>
              <div className={styles.filterCheckboxItem}>
                <input
                  type="checkbox"
                  className={styles.input}
                  checked={showMembersOnly}
                  onChange={(e) =>
                    setShowMembersOnly(e?.target?.checked || false)
                  }
                />
                <p className={styles.label}>Show Active Members Only</p>
              </div>
              <div className={styles.filterItem}>
                <p className={styles.label}>Building</p>
                <select
                  className={styles.input}
                  value={buildingFilter}
                  onChange={(e) => setBuildingFilter(e?.target?.value || "")}
                >
                  <option value="">All</option>
                  {buildings?.map((building: any, i: number) => {
                    return (
                      <option key={i} value={building?.buildingName}>
                        {building?.buildingName}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div className={styles.filterItem}>
                <p className={styles.label}>Status</p>
                <select
                  className={styles.input}
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e?.target?.value || "")}
                >
                  <option value="">All</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">InActive</option>
                </select>
              </div>
            </div>
          </div>
          <div className={styles.gridContainer}>
            <AgGridReact
              className={styles.grid}
              rowData={filteredRowData}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              onGridReady={onGridReady}
              pagination={true}
              paginationPageSize={10}
              paginationPageSizeSelector={[5, 10, 20, 50, 100]}
              animateRows={true}
              domLayout="autoHeight"
              gridOptions={gridOptions}
            />
          </div>
        </div>
      </div>
      <ConfirmationModal
        isOpen={deactivateMembershipConfirmationModalOpen}
        setIsOpen={setDeactivateMembershipConfirmationModalOpen}
        onConfirm={() =>
          memberDeactivateHandler(deactivateMembershipId?.toString())
        }
        heading="Membership Deactivation"
        text="Are you sure you want to deactivate this member's membership?"
        loading={deleteMemberLoading}
      />
      <ConfirmationModal
        isOpen={cancelMembershipConfirmationModalOpen}
        setIsOpen={() => setCancelMembershipConfirmationModalOpen(false)}
        onConfirm={() =>
          cancelMembershipHandler(cancelMembershipId?.toString())
        }
        heading="Membership Cancellation"
        text="Are you sure you want to cancel this member's membership?"
        loading={cancelMembershipLoading}
      />
    </React.Fragment>
  );
}

export default Members;
